import * as angular from "angular";
import { AdminDataProvider } from "../../../data-provider/admin.dp";
import { Campaign } from "../../../models/campaign.model";
import { User } from "../../../models/user.model";
import { ApiUserService } from "../../../services/user.service";
import { UserService } from "../../../services/users.service";
import { CredentialsManager } from "../../../shared/credentials-manager";
import { DialogUtils } from "../../../helper/dialog.helper";
import { ToastUtils } from "../../../helper/toast.helper";

let AdminUsersListComponent = {
    selector: "vcAdminUsersList",
    templateUrl: "/angular/components/admin/lists/users-list.component.html",
    bindings: {
        filter: "=",
    },
    controller: class AdminUsersListController {
        // * bindings for typescript compiler *
        public filter: any;
        // ************************************

        /** Placeholder rows while the first page loads, so the table keeps its height. */

        public skeleton = new Array(8);

        private loading: boolean;
        private allLoaded = false;
        private users: User[];
        private searchCanceller: any;
        public columns: object[];

        private $scope: any;
        private $q: any;
        public __env: any;
        public dataProvider: AdminDataProvider;
        private toast: ToastUtils;
        private dialog: DialogUtils;
        private service: ApiUserService;
        private userService: UserService;
        private credentialsManager: CredentialsManager;
        public searchDelay: any;
        private readonly $interval: any;
        private readonly $filter: any;

        constructor(
            $scope: any,
            $q: any,
            __env: any,
            ApiUserService: ApiUserService,
            AdminDataProvider: AdminDataProvider,
            UserService: UserService,
            ToastUtils: ToastUtils,
            DialogUtils: DialogUtils,
            $interval: any,
            CredentialsManager: CredentialsManager,
            $filter: any
        ) {
            this.$scope = $scope;
            this.$interval = $interval;
            this.$filter = $filter;
            this.$q = $q;
            this.__env = __env;
            this.dataProvider = AdminDataProvider;
            this.service = ApiUserService;
            this.userService = UserService;
            this.toast = ToastUtils;
            this.dialog = DialogUtils;
            this.credentialsManager = CredentialsManager;
            let allColumns = this.dataProvider.getColumns();
            this.columns = allColumns.users;
        }

        $onInit() {
            // Watch only the fields this list actually sends. The shared
            // appliedFilter also carries the campaigns tab's state (efforts,
            // statuses, kinds), and deep-comparing all of it every digest
            // made the whole admin panel sluggish.
            this.$scope.$watch(
                () =>
                    JSON.stringify([
                        this.filter.search && this.filter.search.values,
                        this.filter.onlineOnly,
                        this.filter.thisRegion,
                        this.filter.device && this.filter.device.values,
                        this.filter.accountStatus,
                        this.filter.platform,
                    ]),
                (newValue: object, oldValue: object) => {
                    if (oldValue && newValue !== oldValue) {
                        if (this.searchDelay) {
                            this.searchDelay = this.$interval.cancel(
                                this.searchDelay
                            );
                        }
                        this.searchDelay = this.$interval(() => {
                            this.$interval.cancel(this.searchDelay);
                            if (this.searchCanceller) {
                                this.searchCanceller.resolve("User cancelled");
                            }
                            this.searchCanceller = this.$q.defer();
                            this.loadUsers(true);
                        }, 500);
                    }
                }
            );
            // Unconditional: the shared appliedFilter has thisRegion
            // undefined until a pill is touched, and gating the first load on
            // it left the page empty on a fresh visit.
            this.loadUsers(true);
        }

        /**
         * The server decides, and the Online-only filter uses the same
         * window, so the column and the filter always agree. Comparing
         * last_active here instead would drift by the viewer's UTC offset,
         * because it is a UTC string and lastsync parses it as local time.
         */
        isOnline(user: any): boolean {
            return !!user && !!Number(user.is_online);
        }

        loadUsers(reload: boolean): void {
            if (reload || !this.users || this.users.length == 0) {
                if (reload) this.allLoaded = false;
                this.loading = true;
                this.service
                    .all(
                        { filter: this.filter },
                        this.searchCanceller
                            ? this.searchCanceller.promise
                            : undefined
                    )
                    .then((response: object) => {
                        const json = response["data"];
                        this.loading = false;
                        this.users = json.data;
                        if (json.chipCounts) {
                            // AV-18115: counts ride along with the first page.
                            this.dataProvider.chipCounts.users =
                                json.chipCounts;
                        }
                    });
            }
        }

        scrollLoad(): void {
            if (!this.loading && !this.allLoaded) {
                this.loading = true;
                this.service
                    .all({
                        filter: this.filter,
                        offset: this.users[this.users.length - 1].uid,
                    })
                    .then((response: object) => {
                        const json = response["data"];
                        if (json.data.length) {
                            this.users = this.users.concat(json.data);
                        } else {
                            this.allLoaded = true;
                        }
                        this.loading = false;
                    });
            }
        }

        deleteUser(user: User, $event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        bodyText: "Are you sure you want to delete this user?",
                        title: "Delete User",
                    },
                })
                .then(() => {
                    this.service
                        .remove({ id: user.uid })
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                user.uaccountstatus = "Deleted";
                                this.toast.success("User Deleted");
                            } else {
                                this.toast.error();
                            }
                        });
                });
        }

        reset2fa(user: User, $event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        bodyText: "Are you sure you want to reset 2FA?",
                        title:
                            "Reset 2FA for " + user.ufname + " " + user.ulname,
                    },
                })
                .then(() => {
                    this.userService
                        .reset2fa({ id: user.uid })
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                this.toast.success(
                                    `Two factor authentication reset for ${
                                        user.ufname + " " + user.ulname
                                    }`
                                );
                            } else {
                                this.toast.error(json.message);
                            }
                        });
                });
        }

        requestDiagnostics(user: User, campaign: Campaign, $event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        bodyText:
                            `On its next sync, ${user.ufname} ${user.ulname}'s app will ` +
                            `upload its support-diagnostics report and database for ` +
                            `<b>${campaign.campaignname}</b>. ` +
                            `The request is logged against your account.`,
                        title: "Request Diagnostics",
                        buttonText: { confirm: "Request" },
                    },
                })
                .then(() => {
                    this.userService
                        .requestDiagnostics({
                            id: user.uid,
                            campaignid: campaign.campaignid,
                        })
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                this.toast.success(
                                    json.data && json.data.existing
                                        ? "Already requested — awaiting the device"
                                        : "Diagnostics requested"
                                );
                            } else {
                                this.toast.error(json.message);
                            }
                        });
                });
        }

        resendConfirmation(user: User, $event: any): void {
            this.service
                .resendConfirmation({ email: user.uemail })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.toast.success(
                            `Confirmation email resent to  ${user.uemail}`
                        );
                    } else {
                        this.toast.error(json.message);
                    }
                });
        }

        addToCampaign(campaign: Campaign): void {
            this.userService
                .add({ campaignid: campaign.campaignid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        campaign.member = 1;
                        this.toast.success("Added to Account");
                    } else {
                        this.toast.error(
                            "Failed to add you to account: " +
                                campaign.campaignname
                        );
                    }
                });
        }

        removeFromCampaign(campaign: Campaign): void {
            this.userService
                .self_remove({ id: campaign.campaignid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        if (json.campaign && json.campaign.campaignid) {
                            this.credentialsManager.reauth(
                                json.campaign.campaignid
                            );
                        }
                        campaign.member = 0;
                        this.toast.success("Removed From Account");
                    } else if (json.region && json.handoff) {
                        // Our replacement live campaign is hosted in the
                        // other region - carry the session over rather than
                        // being signed out of both.
                        this.credentialsManager.handoff(
                            json.region,
                            json.handoff,
                            "/admin.php/#/admin_campaigns"
                        );
                    } else {
                        this.toast.error(json.message);
                    }
                });
        }
    },
};

angular
    .module("admin")
    .component(AdminUsersListComponent.selector, AdminUsersListComponent);
